// ─── widgets/button.js ────────────────────────────────────────────────────────
// Creates cleverstory.v2.widgets.button objects.

import shortid from 'shortid';
import { cs, getAbsoluteRect, hasLayoutBox, getFontSize, getFontWeight, getColor, getButtonAlignment } from '../utils/dom.js';
import { extractRGBAFromColor, extractBorderRadius, extractBackground, extractShadowProperties } from '../utils/style.js';
import { resolveFontFamily } from '../utils/fonts.js';
import { createLinkAction } from '../utils/links.js';
import { BUTTON } from 'constants/widgetTypes.jsx';

// The button's link is the <a href> it is, sits inside, or wraps
// (<a data-widget="button" href>, <a href><button/></a>, <div data-widget="button"><a href/></div>).
function extractButtonActions(el) {
    const anchor = el.closest('a[href]') || el.querySelector('a[href]');
    const action = anchor && createLinkAction(anchor);
    return action ? [action] : null;
}

function extractStylesFromEl(el) {
    return {
        text:      el.textContent.trim(),
        fontFamily: cs(el).fontFamily || 'inherit',
        fontSize:   getFontSize(el),
        fontWeight: getFontWeight(el),
        color:      getColor(el),
    };
}

/**
 * @param {{ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap }}
 */
export function createButtonWidgetObj({ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap }) {
    const styles = extractStylesFromEl(el);
    const rect   = getAbsoluteRect(el);
    const mOff = mobileOffsetRect || offsetRect;
    const bx   = Math.round(rect.left  - (offsetRect.left || 0));
    const by   = Math.round(rect.top   - (offsetRect.top  || 0));
    // Hidden on mobile (display:none) → no usable rect; keep the widget hidden
    // there and park it at its desktop offset inside the mobile parent.
    const mobileHidden = !!mobileEl && !hasLayoutBox(mobileEl);
    const mRect  = mobileEl && !mobileHidden
        ? getAbsoluteRect(mobileEl)
        : {
            width:  Math.min(rect.width, 335),
            height: rect.height,
            top:    (mOff.top  || 0) + by,
            left:   (mOff.left || 0) + Math.min(bx, 20),
        };
    const mbx  = Math.round(mRect.left - (mOff.left || 0));
    const mby  = Math.round(mRect.top  - (mOff.top  || 0));

    const style  = cs(el);
    const bgc    = extractBackground(style);
    const fc     = extractRGBAFromColor(styles.color);
    const fs     = styles.fontSize.toString();
    const mfs    = Math.max(styles.fontSize * 0.9, 12).toString();
    const borderWidth = parseFloat(style.borderWidth || '0');
    const normalizedWidth = borderWidth > 0 && borderWidth < 1 ? 1 : borderWidth;
    const border = {
        sides: null,
        color: extractRGBAFromColor(style.borderColor),
        width: normalizedWidth.toString(),
        radius: extractBorderRadius(style),
    };

    const { resolvedFamily, fontIds } = resolveFontFamily(styles.fontFamily, fontFamilyMap, fontIdMap);
    const rff    = resolvedFamily || styles.fontFamily;
    const fstyle = {
        bold:      parseFloat(style.fontWeight) >= 600 || style.fontWeight === 'bold',
        italic:    style.fontStyle === 'italic',
        underline: style.textDecoration.includes('underline'),
    };
    const mkFont = (fontSize) => ({
        style: fstyle, fontSize, fontFamily: rff || null,
        lineSpacing: null, letterSpacing: null, color: fc,
    });

    const shadow = extractShadowProperties(style.boxShadow);

    return {
        uid:           `widget_${shortid.generate()}`,
        type:          BUTTON,
        name:          'Button',
        parent:        parentUid,
        order:         orderRef.value++,
        systemDefined: false,
        fonts:         fontIds.length > 0 ? fontIds : null,
        actions:       extractButtonActions(el),
        parameter:     null,
        properties: {
            desktop: {
                visible: true,
                layout: {
                    transform: { translate: `${bx}px,${by}px`, rotate: '0deg' },
                    width:  Math.round(rect.width)  + 'px',
                    height: Math.round(rect.height) + 'px',
                },
                regular:   { font: mkFont(fs),  background: bgc, border, shadow },
                hover:     { font: mkFont(fs),  background: bgc, border },
                alignment: {
                    position : getButtonAlignment(el),
                    margin: 5
                },
                animation: null,
            },
            mobile: {
                visible: !mobileHidden,
                layout: {
                    transform: { translate: `${mbx}px,${mby}px`, rotate: '0deg' },
                    width:  Math.round(mRect.width)  + 'px',
                    height: Math.round(mRect.height) + 'px',
                },
                regular:   { font: mkFont(mfs), background: bgc, border, shadow },
                hover:     { font: mkFont(mfs), background: bgc, border, shadow: null },
                alignment: {
                    position : getButtonAlignment(mobileEl || el),
                    margin: 5
                },
                animation: null
            },
        },
        content: styles.text || el.value || 'Button',
    };
}